import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, CITIES } from "@/data/config";

// Asymmetric bento grid for the 5 services on the home page.
const IMAGES = {
  "gutter-installation":
    "https://images.unsplash.com/photo-1498847559558-1e4b1a7f7a2f?crop=entropy&cs=srgb&fm=jpg&w=1200",
  "gutter-repair":
    "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?crop=entropy&cs=srgb&fm=jpg&w=1200",
  "gutter-cleaning":
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&w=1200",
  "gutter-guards":
    "https://images.unsplash.com/photo-1524230572899-a752b3835840?crop=entropy&cs=srgb&fm=jpg&w=1200",
  roofing:
    "https://images.unsplash.com/photo-1602193289141-9605ad75d0a5?crop=entropy&cs=srgb&fm=jpg&w=1200",
};

const layout = {
  "gutter-installation": "md:col-span-8 md:row-span-2",
  "gutter-repair": "md:col-span-4",
  "gutter-cleaning": "md:col-span-4",
  "gutter-guards": "md:col-span-4 md:row-span-2",
  roofing: "md:col-span-8",
};

export default function ServicesBento() {
  return (
    <section id="services" data-testid="services-bento" className="relative py-16 md:py-32">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-4">
            <p className="overline mb-4">What we do</p>
          </div>
          <div className="col-span-12 md:col-span-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tightest text-bone-50 max-w-3xl">
              Five services. One crew. Every roof line in the Inland Northwest.
            </h2>
            <Link
              to="/sitemap"
              className="hidden md:inline-flex items-center gap-2 text-sm text-bone-300 hover:text-copper-500 font-heading"
            >
              See all pages
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 md:auto-rows-[280px] gap-4 md:gap-5">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className={`${layout[s.slug]} relative overflow-hidden border border-white/10 group bg-ink-900 rounded-sm`}
            >
              <Link
                to={`/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="block relative w-full h-full min-h-[280px]"
              >
                <img
                  src={IMAGES[s.slug]}
                  alt={`${s.name} in Spokane by Spokane Gutter Co.`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/70 to-transparent" />
                <div className="relative h-full flex flex-col justify-between p-6 md:p-8">
                  <div className="flex items-start justify-between gap-4">
                    <span className="font-display text-4xl md:text-6xl leading-none text-copper-500">
                      {s.accent}
                    </span>
                    <ArrowUpRight className="w-6 h-6 text-bone-200 group-hover:text-copper-500 group-hover:rotate-45 transition-transform duration-300" />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl md:text-4xl leading-tight tracking-tightest mb-2">
                      {s.name}
                    </h3>
                    <p className="text-bone-300 text-sm md:text-base max-w-md">{s.short}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CitiesBand() {
  return (
    <section id="areas" data-testid="cities-band" className="relative py-16 md:py-32 bg-ink-900 border-y border-white/10">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-4">
            <p className="overline mb-4">Where we work</p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tightest text-bone-50 max-w-3xl">
              Six towns. One area code. On the road every morning by seven.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {CITIES.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              className="bg-ink-950"
            >
              <Link
                to={`/${c.slug}`}
                data-testid={`city-card-${c.slug}`}
                className="group block p-6 md:p-10 h-full min-h-[220px] flex flex-col justify-between hover:bg-ink-900 transition-colors duration-200"
              >
                <div className="flex justify-between items-start mb-8">
                  <span className="overline">{c.accent}</span>
                  <ArrowUpRight className="w-5 h-5 text-bone-400 group-hover:text-copper-500 group-hover:rotate-45 transition-transform duration-300" />
                </div>
                <div>
                  <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tightest mb-2">
                    {c.name}
                  </h3>
                  <p className="text-bone-300 text-sm">{c.subtitle}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
